<template>
  <div class="login-fullscreen-wrapper">
    <div class="login-container">
      <div class="login-content-wrapper">
        <!-- Login Card -->
        <div class="login-card-wrapper">
          <div class="card shadow-lg border-0">
            <div class="card-header p-0 position-relative mt-n4 mx-3 z-index-2">
              <div
                class="bg-gradient-brand shadow-brand border-radius-lg py-3 pe-1"
              >
                <span class="center-logo"
                  ><img
                    src="@/assets/img/lgrb-logo.png"
                    class="navbar-brand-img h-120"
                    alt="NCEMS Middleware Operator Portal"
                    style="height: 80px"
                /></span>
                <h3 class="text-white font-weight-bold text-center mt-2 mb-0">
                  {{
                    isTwoFactorStage
                      ? "Verification Code"
                      : " NCEMS Middleware Operator Portal"
                  }}
                </h3>
                <p
                  class="text-white text-sm text-center mt-2 mb-2 px-3 opacity-9"
                >
                  {{
                    isTwoFactorStage
                      ? "Enter the code sent to your email"
                      : "Welcome to the NCEMS AI Middleware Portal. Sign in to access your dashboard and manage your operations."
                  }}
                </p>
              </div>
            </div>
            <div class="card-body px-4 py-4">
              <div style="display: none; height: 0; overflow: hidden">
                <input type="text" name="dummy_username" autocomplete="off" />
                <input
                  type="password"
                  name="dummy_password"
                  autocomplete="off"
                />
              </div>

              <!-- Error Message -->
              <div
                v-if="error"
                class="alert alert-danger alert-dismissible fade show"
                role="alert"
              >
                <div class="alert-content">
                  <i class="fas fa-exclamation-circle"></i>
                  <span>{{ error }}</span>
                </div>
                <button
                  type="button"
                  class="btn-close"
                  @click="error = ''"
                  aria-label="Close"
                ></button>
              </div>

              <!-- Success Message -->
              <div
                v-if="successMessage"
                class="alert alert-success alert-dismissible fade show"
                role="alert"
              >
                <div class="alert-content">
                  <i class="fas fa-check-circle"></i>
                  <span>{{ successMessage }}</span>
                </div>
                <button
                  type="button"
                  class="btn-close"
                  @click="successMessage = ''"
                  aria-label="Close"
                ></button>
              </div>

              <!-- LOGIN FORM -->
              <form
                v-if="!isTwoFactorStage"
                class="text-start"
                autocomplete="off"
                @submit.prevent="handleSignIn"
              >
                <div class="mb-3">
                  <label class="form-label">Email Address</label>
                  <material-input
                    id="login_email"
                    :type="emailFieldType"
                    :value="email"
                    @input="handleEmailInput"
                    @focus="changeEmailFieldType"
                    placeholder="Enter your email"
                  />
                </div>

                <div class="mb-3">
                  <label class="form-label">Password</label>
                  <div class="position-relative">
                    <material-input
                      :type="passwordVisible ? 'text' : 'password'"
                      :value="password"
                      @input="handlePasswordInput"
                      placeholder="Enter your password"
                    />
                    <button
                      type="button"
                      class="password-toggle-btn"
                      @click="togglePasswordVisibility"
                      tabindex="-1"
                    >
                      <i
                        :class="
                          passwordVisible ? 'fas fa-eye-slash' : 'fas fa-eye'
                        "
                      ></i>
                    </button>
                  </div>
                </div>

                <!-- Remember me -->
                <div class="mb-3">
                  <material-switch id="rememberMe" name="remember_me_check">
                    Remember me
                  </material-switch>
                </div>

                <div class="text-center mt-4">
                  <material-button
                    variant="gradient"
                    color="success"
                    fullWidth
                    type="submit"
                    :disabled="loader"
                  >
                    <span v-if="loader">
                      <i class="fas fa-spinner fa-spin me-2"></i>
                      Signing in...
                    </span>
                    <span v-else>
                      <i class="fas fa-sign-in-alt me-2"></i>
                      Sign In
                    </span>
                  </material-button>
                </div>

                <!-- Sign up and forgot password links -->
                <div class="text-center mt-3">
                  <p class="mb-2 text-sm text-muted">
                    Don't have an account?
                    <router-link
                      :to="{ name: 'SignUp' }"
                      class="text-brand font-weight-bold ms-1"
                    >
                      Create Account
                    </router-link>
                  </p>
                  <p class="text-sm mb-0">
                    <router-link
                      :to="{ name: 'Forgot Password' }"
                      class="text-brand font-weight-bold"
                    >
                      <i class="fas fa-key me-1"></i>
                      Forgot your password?
                    </router-link>
                  </p>
                </div>
              </form>

              <!-- 2FA FORM -->
              <form
                v-else
                class="text-start"
                autocomplete="off"
                @submit.prevent="verifyOTP"
              >
                <div class="text-center mb-3">
                  <p class="text-sm text-muted mb-2">
                    Please enter the 6-digit verification code sent to your
                    email.
                  </p>
                  <div
                    v-if="otpDeliveryInfo"
                    class="alert alert-info py-2 mt-2"
                  >
                    <small>
                      <i class="fas fa-info-circle me-1"></i>
                      {{ otpDeliveryInfo }}
                    </small>
                  </div>
                </div>

                <!-- OTP BOXES -->
                <div class="mb-3">
                  <div class="otp-container">
                    <input
                      v-for="(digit, index) in otpDigits"
                      :key="index"
                      ref="otpInputs"
                      type="text"
                      inputmode="numeric"
                      maxlength="1"
                      class="otp-box"
                      :class="{ 'is-invalid': otpError }"
                      v-model="otpDigits[index]"
                      @input="onOtpInput(index)"
                      @keydown.backspace="onOtpBackspace(index)"
                      @paste.prevent="onOtpPaste"
                    />
                  </div>

                  <div class="text-center mt-2">
                    <small class="text-muted">
                      {{ otpTokenLength }}/6 digits entered
                    </small>
                  </div>

                  <div
                    v-if="otpError"
                    class="text-danger text-sm mt-2 text-center"
                  >
                    <i class="fas fa-exclamation-circle me-1"></i>
                    {{ otpError }}
                  </div>
                </div>

                <!-- Action Buttons -->
                <div
                  class="d-flex justify-content-between align-items-center mb-3"
                >
                  <button
                    type="button"
                    @click="goBackToLogin"
                    :disabled="loader"
                    class="btn-link-custom"
                  >
                    <i class="fas fa-arrow-left me-1"></i>
                    Back to Login
                  </button>

                  <button
                    type="button"
                    @click="resendOTP"
                    :disabled="loader || resendCountdown > 0"
                    class="btn-link-custom"
                    :class="{ 'text-muted': loader || resendCountdown > 0 }"
                  >
                    <i class="fas fa-redo-alt me-1"></i>
                    Resend Code
                  </button>
                </div>

                <div v-if="resendCountdown > 0" class="text-center mb-3">
                  <small class="text-muted">
                    Resend available in {{ formatCountdown }}
                  </small>
                </div>

                <div class="text-center">
                  <material-button
                    variant="gradient"
                    color="success"
                    fullWidth
                    type="submit"
                    :disabled="loader || otpTokenLength !== 6"
                  >
                    <span v-if="loader">
                      <i class="fas fa-spinner fa-spin me-2"></i>
                      Verifying...
                    </span>
                    <span v-else>
                      <i class="fas fa-check-circle me-2"></i>
                      Verify & Continue
                    </span>
                  </material-button>
                </div>
              </form>
            </div>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import MaterialButton from "@/components/MaterialButton.vue";
import MaterialInput from "@/components/MaterialInput.vue";
import MaterialSwitch from "@/components/MaterialSwitch.vue";
import axios from "axios";

export default {
  name: "sign-in",
  components: { MaterialButton, MaterialInput, MaterialSwitch },

  data() {
    return {
      isTwoFactorStage: false,
      email: "",
      password: "",
      emailFieldType: "text",
      passwordVisible: false,
      rememberMe: false,

      otpDigits: ["", "", "", "", "", ""],
      otpError: "",
      sessionId: "",
      otpDeliveryInfo: "",

      loader: false,
      error: "",
      successMessage: "",

      resendCountdown: 60,
      resendTimer: null,

      tempPassword: "",
    };
  },

  computed: {
    otpToken() {
      return this.otpDigits.join("");
    },
    otpTokenLength() {
      return this.otpToken.length;
    },
    formatCountdown() {
      const minutes = Math.floor(this.resendCountdown / 60);
      const seconds = this.resendCountdown % 60;
      return `${minutes}:${seconds.toString().padStart(2, "0")}`;
    },
  },

  mounted() {
    document.body.classList.remove("g-sidenav-show", "g-sidenav-pinned");
    document.body.style.overflow = "hidden";

    const pendingOTP = localStorage.getItem("pending_otp_session");
    if (pendingOTP) {
      try {
        const session = JSON.parse(pendingOTP);
        if (Date.now() - session.timestamp < 300000) {
          this.sessionId = session.sessionId;
          this.email = session.email;
          this.isTwoFactorStage = true;
          this.startResendTimer();
          this.otpDeliveryInfo = session.deliveryInfo || "";
        } else {
          localStorage.removeItem("pending_otp_session");
        }
      } catch (e) {
        localStorage.removeItem("pending_otp_session");
      }
    }

    const rememberedEmail = localStorage.getItem("remembered_email");
    if (rememberedEmail) {
      this.email = rememberedEmail;
      this.rememberMe = true;
    }
  },

  beforeUnmount() {
    this.tempPassword = "";
    document.body.style.overflow = "";
    this.clearResendTimer();
  },

  methods: {
    togglePasswordVisibility() {
      this.passwordVisible = !this.passwordVisible;
    },

    handleEmailInput(e) {
      this.email = e.target.value;
      if (this.rememberMe) {
        localStorage.setItem("remembered_email", this.email);
      }
    },

    handlePasswordInput(e) {
      this.password = e.target.value;
    },

    changeEmailFieldType() {
      this.emailFieldType = "email";
    },

    async handleSignIn() {
      this.loader = true;
      this.error = "";
      this.successMessage = "";

      try {
        if (!this.email || !this.password) {
          this.error = "Please enter both email and password";
          return;
        }

        const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
        if (!emailRegex.test(this.email)) {
          this.error = "Please enter a valid email address";
          return;
        }

        if (this.rememberMe) {
          localStorage.setItem("remembered_email", this.email);
        } else {
          localStorage.removeItem("remembered_email");
        }

        this.tempPassword = this.password;

        const res = await axios.post("/get-otp/", {
          username: this.email,
          password: this.password,
        });

        this.sessionId = res.data.session_id;
        this.otpDeliveryInfo = res.data.message || "OTP sent to your email";

        localStorage.setItem(
          "pending_otp_session",
          JSON.stringify({
            sessionId: this.sessionId,
            email: this.email,
            deliveryInfo: this.otpDeliveryInfo,
            timestamp: Date.now(),
          }),
        );

        this.isTwoFactorStage = true;
        this.successMessage = res.data.message || "Verification code sent";

        this.startResendTimer();

        setTimeout(() => {
          this.successMessage = "";
        }, 3000);
      } catch (error) {
        console.error("Login error:", error);
        this.tempPassword = "";
        this.handleLoginError(error);
      } finally {
        this.loader = false;
      }
    },

    onOtpInput(index) {
      const value = this.otpDigits[index].toString().replace(/\D/g, "");
      this.otpDigits[index] = value.slice(-1);
      this.otpError = "";

      if (this.otpDigits[index] && index < 5) {
        this.$refs.otpInputs[index + 1].focus();
      }

      if (this.otpTokenLength === 6) {
        setTimeout(() => this.verifyOTP(), 300);
      }
    },

    onOtpBackspace(index) {
      if (!this.otpDigits[index] && index > 0) {
        this.$refs.otpInputs[index - 1].focus();
      }
    },

    onOtpPaste(e) {
      const paste = e.clipboardData
        .getData("text")
        .replace(/\D/g, "")
        .slice(0, 6);
      paste.split("").forEach((d, i) => (this.otpDigits[i] = d));
      if (paste.length === 6) this.verifyOTP();
    },

    async verifyOTP() {
      if (this.otpTokenLength !== 6) return;

      this.loader = true;
      this.error = "";
      this.otpError = "";

      try {
        const otpPayload = {
          session_id: this.sessionId,
          otp: this.otpToken,
          username: this.email,
          password: this.tempPassword,
        };

        console.log("Sending OTP verification:", {
          session_id: otpPayload.session_id,
          otp: otpPayload.otp,
          username: otpPayload.username,
        });

        const response = await axios.post("/token/verify-otp/", otpPayload);

        localStorage.removeItem("pending_otp_session");
        this.tempPassword = "";
        this.clearResendTimer();

        const user = response.data.user || {
          id: response.data.user_id,
          email: this.email,
          name: response.data.name || this.email.split("@")[0],
        };

        const tokens = response.data.tokens || {
          access: response.data.access_token || response.data.access,
          refresh: response.data.refresh_token || response.data.refresh,
        };

        this.$store.dispatch("auth/login", { user, tokens });

        axios.defaults.headers.common[
          "Authorization"
        ] = `Bearer ${tokens.access}`;

        this.successMessage = "Login successful! Redirecting...";

        setTimeout(() => {
          this.$router.push("/dashboard");
        }, 1000);
      } catch (error) {
        console.error("OTP verification error:", error);
        console.error("Error response:", error.response?.data);
        this.tempPassword = "";
        this.handleOtpVerificationError(error);
        this.otpDigits = ["", "", "", "", "", ""];
      } finally {
        this.loader = false;
      }
    },

    async resendOTP() {
      if (this.resendCountdown > 0) {
        return;
      }

      this.loader = true;
      this.error = "";
      this.successMessage = "";

      try {
        const response = await axios.post("/token/resend-otp/", {
          session_id: this.sessionId,
          email: this.email,
        });

        if (response.data.session_id) {
          this.sessionId = response.data.session_id;
        }

        this.otpDeliveryInfo =
          response.data.delivery_info || response.data.message || "";
        this.successMessage = "New verification code sent successfully";

        this.resendCountdown = 60;
        this.startResendTimer();

        localStorage.setItem(
          "pending_otp_session",
          JSON.stringify({
            sessionId: this.sessionId,
            email: this.email,
            deliveryInfo: this.otpDeliveryInfo,
            timestamp: Date.now(),
          }),
        );

        setTimeout(() => {
          this.successMessage = "";
        }, 3000);
      } catch (error) {
        console.error("Resend OTP error:", error);
        this.error = "Failed to resend code. Please try again.";
      } finally {
        this.loader = false;
      }
    },

    goBackToLogin() {
      localStorage.removeItem("pending_otp_session");
      this.tempPassword = "";

      this.isTwoFactorStage = false;
      this.otpDigits = ["", "", "", "", "", ""];
      this.sessionId = "";
      this.otpDeliveryInfo = "";
      this.error = "";
      this.successMessage = "";
      this.otpError = "";

      this.clearResendTimer();
    },

    startResendTimer() {
      this.clearResendTimer();

      this.resendTimer = setInterval(() => {
        if (this.resendCountdown > 0) {
          this.resendCountdown--;
        } else {
          this.clearResendTimer();
        }
      }, 1000);
    },

    clearResendTimer() {
      if (this.resendTimer) {
        clearInterval(this.resendTimer);
        this.resendTimer = null;
      }
    },

    handleLoginError(error) {
      if (error.response) {
        switch (error.response.status) {
          case 400:
          case 401:
            this.error = "Invalid email or password";
            break;
          case 403:
            this.error = "Account disabled or not verified";
            break;
          case 404:
            this.error = "User not found";
            break;
          case 429:
            this.error = "Too many attempts. Please try again later";
            break;
          case 500:
            this.error = "Server error. Please try again later";
            break;
          default:
            this.error = "Login failed. Please check your credentials";
        }
      } else if (error.request) {
        this.error = "Network error. Please check your connection.";
      } else {
        this.error = "An error occurred. Please try again.";
      }
    },

    handleOtpVerificationError(error) {
      if (error.response) {
        switch (error.response.status) {
          case 400:
          case 401:
            this.otpError = "Invalid or expired verification code";
            break;
          case 403:
          case 404:
            this.otpError = "Session expired";
            this.goBackToLogin();
            break;
          case 410:
            this.otpError = "Code expired. Please request a new one";
            break;
          case 429:
            this.otpError = "Too many attempts. Please try again later";
            break;
          default:
            this.otpError = "Verification failed. Please try again.";
        }
      } else {
        this.otpError = "Network error. Please try again.";
      }
    },
  },
};
</script>

<style scoped>
.center-logo {
  display: flex;
  justify-content: center;
  align-items: center;
}

.login-fullscreen-wrapper {
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: linear-gradient(
    135deg,
    #f8f9fa 0%,
    #e9ecef 25%,
    #dee2e6 50%,
    #e9ecef 75%,
    #f8f9fa 100%
  );
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 9999;
  padding: 1.5rem;
  overflow-y: auto;
}

.login-container {
  width: 100%;
  max-width: 520px;
  margin: 0 auto;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  min-height: auto;
}

.login-content-wrapper {
  width: 100%;
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 2rem;
}

/* Logo Section */
.logo-section {
  text-align: center;
  margin-bottom: 0.5rem;
}

.logo-wrapper {
  width: 120px;
  height: 120px;
  margin: 0 auto;
  display: flex;
  align-items: center;
  justify-content: center;
}

.logo-image {
  width: 100%;
  height: 100%;
  object-fit: contain;
}

/* Login Card */
.login-card-wrapper {
  width: 100%;
  max-width: 480px;
}

.card {
  border-radius: 1.25rem;
  border: none;
  box-shadow: 0 20px 60px 0 rgba(0, 0, 0, 0.08);
  overflow: visible;
  transition: all 0.3s ease;
  background: #ffffff;
}

.card:hover {
  box-shadow: 0 25px 70px 0 rgba(0, 0, 0, 0.12);
}

.card-header > div {
  min-height: auto !important;
  padding: 1.5rem 1.5rem !important;
  border-radius: 1rem !important;
}

.card-header h3 {
  font-size: 1.5rem;
  margin-top: 0.5rem !important;
  font-weight: 700;
}

.card-header p {
  font-size: 0.8125rem;
  margin-top: 0.5rem !important;
}

.card-body {
  padding: 2rem !important;
}

/* Brand Colors */
.bg-gradient-brand {
  /* background: linear-gradient(310deg, #dc3545 0%, #ffc107 100%); */
  background: #000000;
}

.shadow-brand {
  /* box-shadow:
    0 4px 20px 0 rgba(220, 53, 69, 0.18),
    0 7px 10px -5px rgba(255, 193, 7, 0.4); */
  box-shadow:
    0 4px 20px 0 rgba(0, 0, 0, 0.18),
    0 7px 10px -5px rgba(0, 0, 0, 0.4);
}

.text-brand {
  color: #dc3545;
  transition: all 0.2s ease;
}

.text-brand:hover {
  color: #ffc107;
}

/* Alerts */
.alert {
  border-radius: 0.75rem;
  border: none !important;
  font-size: 0.875rem;
  padding: 0.875rem 1rem !important;
  margin-bottom: 1rem !important;
}

.alert-content {
  display: flex;
  align-items: center;
  gap: 0.75rem;
}

.alert-content i {
  font-size: 1.25rem;
}

.alert.alert-success {
  background: linear-gradient(135deg, #d4edda 0%, #c3e6cb 100%) !important;
  color: #155724 !important;
  border-left: 4px solid #28a745 !important;
}

.alert.alert-danger {
  background: linear-gradient(135deg, #f8d7da 0%, #f5c6cb 100%) !important;
  color: #721c24 !important;
  border-left: 4px solid #dc3545 !important;
}

.alert-info {
  background: linear-gradient(135deg, #d1ecf1 0%, #bee5eb 100%) !important;
  border-left: 4px solid #17a2b8 !important;
  color: #0c5460 !important;
}

/* Form Elements */
.form-label {
  font-weight: 600;
  font-size: 0.875rem;
  color: #344767;
  margin-bottom: 0.5rem;
}

/* Password Toggle */
.password-toggle-btn {
  position: absolute;
  right: 16px;
  top: 65%;
  transform: translateY(-50%);
  background: none;
  border: none;
  padding: 8px;
  cursor: pointer;
  z-index: 20;
  display: flex;
  align-items: center;
  justify-content: center;
  color: #6c757d;
  transition: all 0.2s ease;
}

.password-toggle-btn:hover {
  color: #dc3545;
}

.password-toggle-btn i {
  font-size: 1.1rem;
}

/* OTP Section */
.otp-icon-wrapper {
  width: 70px;
  height: 70px;
  background: linear-gradient(135deg, #dc3545 0%, #ffc107 100%);
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  margin: 0 auto;
  color: white;
  font-size: 2rem;
}

.otp-container {
  display: flex;
  justify-content: center;
  gap: 0.5rem;
  margin: 1rem 0;
}

.otp-box {
  width: 50px;
  height: 50px;
  text-align: center;
  font-size: 1.5rem;
  font-weight: 700;
  border: 2px solid #dee2e6;
  border-radius: 0.75rem;
  outline: none;
  transition: all 0.2s ease;
  background: #ffffff;
  color: #344767;
}

.otp-box:focus {
  border-color: #dc3545;
  box-shadow: 0 0 0 4px rgba(220, 53, 69, 0.15);
  transform: scale(1.05);
}

.otp-box.is-invalid {
  border-color: #dc3545;
  background: #fff5f5;
}

/* Custom Link Buttons */
.btn-link-custom {
  background: none;
  border: none;
  padding: 0;
  cursor: pointer;
  text-decoration: none;
  font-size: 0.875rem;
  color: #dc3545;
  font-weight: 600;
  transition: all 0.2s ease;
}

.btn-link-custom:hover:not(:disabled) {
  color: #ffc107;
}

.btn-link-custom:disabled {
  opacity: 0.6;
  cursor: not-allowed;
}

/* Secure Notice */
.secure-notice {
  padding: 0.75rem;
  background: #f8f9fa;
  border-radius: 0.5rem;
  color: #6c757d;
}

.secure-notice i {
  color: #28a745;
}

/* Responsive */
@media (max-width: 576px) {
  .login-fullscreen-wrapper {
    padding: 1rem;
  }

  .login-content-wrapper {
    gap: 1.5rem;
  }

  .logo-wrapper {
    width: 100px;
    height: 100px;
  }

  .card-body {
    padding: 1.5rem !important;
  }

  .card-header h3 {
    font-size: 1.375rem;
  }

  .otp-box {
    width: 45px;
    height: 45px;
    font-size: 1.375rem;
  }

  .otp-container {
    gap: 0.4rem;
  }

  .otp-icon-wrapper {
    width: 60px;
    height: 60px;
    font-size: 1.75rem;
  }
}
</style>

<style>
.fullscreen-login {
  margin-left: 0 !important;
  width: 100% !important;
}

.login-fullscreen-wrapper ~ .sidenav,
.login-fullscreen-wrapper ~ .navbar,
.login-fullscreen-wrapper ~ footer {
  display: none !important;
  opacity: 0 !important;
  visibility: hidden !important;
}

/* MaterialSwitch custom styles */
.material-switch :deep(.form-check-input:checked) {
  background-color: #dc3545;
  border-color: #dc3545;
}

.material-switch :deep(.form-check-input:focus) {
  box-shadow: 0 0 0 0.25rem rgba(220, 53, 69, 0.25);
}
</style>
